import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  StudentOs,
  StudentEsa,
  ProgramType,
  ResendAwardOfferResponse,
  ScholarshipApplicationPrint,
  ExitStudentProgramRequest
} from '@k12/models';
import { SHARED_ENVIRONMENT } from '@k12/app-services';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class StudentAwardService {
  private readonly http = inject(HttpClient);
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  getOs(studentId: string, academicYearId: number | null): Observable<StudentOs> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    return this.http.get<StudentOs>(`${this.apiUrl}/household/student/${studentId}/os`, { params });
  }

  saveOsStudentDetail(studentId: string, payload: StudentOs, academicYearId: number | null = null): Observable<void> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    return this.http.put<void>(`${this.apiUrl}/household/student/${studentId}/os`, payload, { params });
  }

  getEsa(studentId: string, academicYearId: number | null): Observable<StudentEsa> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    return this.http.get<StudentEsa>(`${this.apiUrl}/household/student/${studentId}/esa`, { params });
  }

  saveEsaStudentDetail(studentId: string, payload: StudentEsa, academicYearId: number | null = null): Observable<void> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    return this.http.put<void>(`${this.apiUrl}/household/student/${studentId}/esa`, payload, { params });
  }

  exitProgram(studentId: string, request: ExitStudentProgramRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/household/student/${studentId}/exit`, request);
  }

  resendAwardOffer(studentId: string, programType: ProgramType): Observable<ResendAwardOfferResponse> {
    const code: 'ESA' | 'OS' = programType === ProgramType.ESA ? 'ESA' : 'OS';
    return this.http.post<ResendAwardOfferResponse>(
      `${this.apiUrl}/household/student/${studentId}/resend-award-offer`,
      null,
      { params: { programTypeCode: code } }
    );
  }

  public getScholarshipApplicationPrint(studentId: string,programType: ProgramType, academicYearId: number | null = null): Observable<ScholarshipApplicationPrint> {
    const code: 'ESA' | 'OS' = programType === ProgramType.ESA ? 'ESA' : 'OS';

    let url = `${this.apiUrl}/student/${studentId}/application/print/${code}`;

    if(academicYearId !== null) {
      url += `/${academicYearId}`;
    }

    return this.http
    .get<{ entity: ScholarshipApplicationPrint }>(
      url,
    )
    .pipe(map((response) => response.entity));
  }
}
